export enum GtmEventName {
  // Custom Events
  ApplyCoupon = 'apply_coupon',
  ConfigurationChange = 'configuration_change',
  ConfigurationSummary = 'configuration_summary',
  DefaultConfiguration = 'default_configuration',
  // Ecommerce Events
  ActivateLead = 'activate_lead',
  AddPaymentInfo = 'add_payment_info',
  AddShippingInfo = 'add_shipping_info',
  BeginCheckout = 'begin_checkout',
  GenerateLead = 'generate_lead',
  Purchase = 'purchase',
  SelectItem = 'select_item',
  ViewItem = 'view_item',
  ViewItemList = 'view_item_list',
}

export type GtmEvent =
  | GtmActivateLeadEvent
  | GtmAddPaymentInfoEvent
  | GtmAddShippingInfoEvent
  | GtmApplyCouponEvent
  | GtmBeginCheckoutEvent
  | GtmConfigurationChangeEvent
  | GtmConfigurationSummaryEvent
  | GtmDefaultConfigurationEvent
  | GtmGenerateLeadEvent
  | GtmPurchaseEvent
  | GtmSelectItemEvent
  | GtmViewItemEvent
  | GtmViewItemListEvent;

export interface GtmEcommerceItem {
  item_id: string;
  item_name: string;
  index?: number;
  item_brand?: string;
  item_purchasable?: boolean;
  item_variant?: string;
  quantity?: number;
  coupon?: string;
  discount?: number;
  item_category?: string;
  item_category2?: string;
  item_category3?: string;
  item_category4?: string;
  item_category5?: string;
  item_list_name?: string;
  price?: number;
}

export interface GtmConfigurationItem {
  feature_code: string;
  feature_name: string;
  feature_option_code: string;
  feature_option_name: string;
}

export interface GtmActivateLeadEvent {
  event: GtmEventName.ActivateLead;
  ecommerce: {
    currency: string;
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
  };
}

interface GtmAddPaymentInfoEvent {
  event: GtmEventName.AddPaymentInfo;
  ecommerce: {
    coupon: string;
    currency: string;
    /** Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here */
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
    payment_type: string;
    value: number;
  };
}

interface GtmAddShippingInfoEvent {
  event: GtmEventName.AddShippingInfo;
  ecommerce: {
    coupon: string;
    currency: string;
    /** Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here */
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
    shipping_tier: string;
    value: number;
  };
}

interface GtmApplyCouponEvent {
  event: GtmEventName.ApplyCoupon;
  custom_event: {
    coupon: string;
    currency: string;
    /** Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here */
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
    value: number;
  };
}

interface GtmDefaultConfigurationEvent {
  event: GtmEventName.DefaultConfiguration;
  custom_event: {
    item_id: string;
    item_name: string;
    item_purchasable: boolean;
    items: GtmConfigurationItem[];
    language_code: string;
  };
}

interface GtmConfigurationChangeEvent {
  event: GtmEventName.ConfigurationChange;
  custom_event: {
    feature_change: GtmConfigurationItem;
    item_id: string;
    item_name: string;
    item_purchasable: boolean;
    items: GtmConfigurationItem[];
    language_code: string;
  };
}

interface GtmConfigurationSummaryEvent {
  event: GtmEventName.ConfigurationSummary;
  custom_event: {
    configuration_code: string;
    item_id: string;
    item_name: string;
    item_purchasable: boolean;
    items: GtmConfigurationItem[];
    language_code: string;
  };
}

interface GtmBeginCheckoutEvent {
  event: GtmEventName.BeginCheckout;
  ecommerce: {
    coupon: string;
    currency: string;
    /** Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here */
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
    value: number;
  };
}

export interface GtmGenerateLeadEvent {
  event: GtmEventName.GenerateLead;
  ecommerce: {
    consent_granted: 'Yes' | 'No';
    country_name: string;
    currency: string;
    customer_type: string;
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
  };
}

interface GtmPurchaseEvent {
  event: GtmEventName.Purchase;
  ecommerce: {
    coupon: string;
    currency: string;
    /** Only one product can be part of the checkout process for now, so item_name and item_purchasable are safe to send here */
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
    shipping: number;
    tax: number;
    transaction_id: string;
    value: number;
  };
}

interface GtmSelectItemEvent {
  event: GtmEventName.SelectItem;
  ecommerce: {
    currency: string;
    item_list_name: string;
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
  };
}

interface GtmViewItemEvent {
  event: GtmEventName.ViewItem;
  ecommerce: {
    currency: string;
    item_name: string;
    item_purchasable: boolean;
    items: GtmEcommerceItem[];
    value: number;
  };
}

interface GtmViewItemListEvent {
  event: GtmEventName.ViewItemList;
  ecommerce: {
    currency: string;
    item_list_name: string;
    items: GtmEcommerceItem[];
  };
}
